const MEGABYTE = 1024 * 1024;

export const MAX_MESSAGE_IMAGE_SIZE = 20 * MEGABYTE;

const HEIC_MIME_TYPES = ['image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence'];
const SHARETRIBE_IMAGE_MIME_TYPES = ['image/jpeg', 'image/png', 'image/gif'];
const MIME_TYPE_BY_EXTENSION = {
  gif: 'image/gif',
  heic: 'image/heic',
  heif: 'image/heif',
  jpeg: 'image/jpeg',
  jpg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
};

export class MessageAttachmentError extends Error {
  constructor(code, message) {
    super(message);
    this.name = 'MessageAttachmentError';
    this.code = code;
  }
}

export const getFileExtension = fileName => {
  const match = typeof fileName === 'string' && fileName.match(/\.([^.]+)$/);
  return match ? match[1].toLowerCase() : '';
};

export const getMessageImageMimeType = file => {
  const suppliedType = typeof file?.type === 'string' ? file.type.toLowerCase() : '';
  const extensionType = MIME_TYPE_BY_EXTENSION[getFileExtension(file?.name)];

  if (suppliedType === 'image/jpg') {
    return 'image/jpeg';
  }

  // Mobile pickers occasionally expose a useful filename but only provide a
  // generic binary MIME type. Prefer the recognized image extension in that case.
  if (!suppliedType || suppliedType === 'application/octet-stream') {
    return extensionType || suppliedType;
  }

  return suppliedType;
};

export const isHeicOrHeifImage = file => {
  const mimeType = getMessageImageMimeType(file);
  const extension = getFileExtension(file?.name);
  const suppliedType = typeof file?.type === 'string' ? file.type.toLowerCase() : '';

  if (HEIC_MIME_TYPES.includes(mimeType)) {
    return true;
  }

  // Photos pickers may transcode HEIC bytes to a genuine JPEG while retaining
  // the original filename. A concrete supported MIME type takes precedence.
  if (SHARETRIBE_IMAGE_MIME_TYPES.includes(mimeType) && suppliedType) {
    return false;
  }

  return ['heic', 'heif'].includes(extension);
};

const convertedJpegName = fileName => {
  const name = typeof fileName === 'string' && fileName.trim() ? fileName.trim() : 'attachment';
  return `${name.replace(/\.[^.]+$/, '')}.jpg`;
};

const fileWithMimeType = (file, mimeType, fileName = file?.name) => {
  if (file.type === mimeType && file.name === fileName) {
    return file;
  }

  return new File([file], fileName, {
    type: mimeType,
    lastModified: file.lastModified || Date.now(),
  });
};

const blobToJpegFile = (blob, sourceFile) => {
  if (!(blob instanceof Blob)) {
    throw new MessageAttachmentError(
      'conversion-failed',
      'The selected photo could not be converted.'
    );
  }

  return new File([blob], convertedJpegName(sourceFile?.name), {
    type: 'image/jpeg',
    lastModified: sourceFile?.lastModified || Date.now(),
  });
};

const convertBrowserImageToJpeg = file => {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();

    const cleanUp = () => URL.revokeObjectURL(objectUrl);
    const rejectConversion = () => {
      cleanUp();
      reject(
        new MessageAttachmentError('unsupported-format', 'This image format is not supported.')
      );
    };

    image.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = image.naturalWidth || image.width;
      canvas.height = image.naturalHeight || image.height;
      const context = canvas.getContext('2d');

      if (!context || !canvas.width || !canvas.height) {
        rejectConversion();
        return;
      }

      context.drawImage(image, 0, 0);
      canvas.toBlob(
        blob => {
          cleanUp();
          try {
            resolve(blobToJpegFile(blob, file));
          } catch (error) {
            reject(error);
          }
        },
        'image/jpeg',
        0.9
      );
    };
    image.onerror = rejectConversion;
    image.src = objectUrl;
  });
};

const convertHeicToJpeg = async file => {
  // heic2any touches browser globals when its module is evaluated. Loading it only
  // for HEIC/HEIF files keeps server-side rendering safe and the normal bundle small.
  const { default: heic2any } = await import('heic2any');
  const conversionResult = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.9 });
  const blob = Array.isArray(conversionResult) ? conversionResult[0] : conversionResult;
  return blobToJpegFile(blob, file);
};

const validateFileSize = file => {
  if (typeof file?.size !== 'number' || file.size <= 0) {
    throw new MessageAttachmentError('invalid-file', 'The selected photo is empty or invalid.');
  }
  if (file.size > MAX_MESSAGE_IMAGE_SIZE) {
    throw new MessageAttachmentError('file-too-large', 'The selected photo is larger than 20 MB.');
  }
};

/**
 * Converts browser image formats that Sharetribe does not accept into JPEG.
 * Sharetribe's image endpoint accepts JPEG, PNG, and non-animated GIF files.
 */
export const prepareMessageImageForUpload = async (
  file,
  { heicConverter = convertHeicToJpeg, browserImageConverter = convertBrowserImageToJpeg } = {}
) => {
  if (!file) {
    throw new MessageAttachmentError('invalid-file', 'No photo was selected.');
  }

  validateFileSize(file);

  const mimeType = getMessageImageMimeType(file);
  let preparedFile;

  try {
    if (isHeicOrHeifImage(file)) {
      preparedFile = await heicConverter(file);
    } else if (SHARETRIBE_IMAGE_MIME_TYPES.includes(mimeType)) {
      preparedFile = fileWithMimeType(file, mimeType);
    } else if (mimeType.startsWith('image/')) {
      preparedFile = await browserImageConverter(file);
    } else {
      throw new MessageAttachmentError(
        'unsupported-format',
        'Please select a JPEG, PNG, GIF, WebP, HEIC, or HEIF image.'
      );
    }
  } catch (error) {
    if (error instanceof MessageAttachmentError) {
      throw error;
    }
    throw new MessageAttachmentError(
      'conversion-failed',
      'The selected photo could not be converted. Please try a screenshot or JPEG instead.'
    );
  }

  validateFileSize(preparedFile);
  return preparedFile;
};
